import React, { useState, useRef } from 'react'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import { Button, ListItem, Menu } from '@material-ui/core'
import isEqual from 'lodash/isEqual'
import { addDays, addMonths, addWeeks, subDays } from 'date-fns'
import { setSelectedFilters } from 'reducers/lookupTables/reducer'
import {
  dateToLocalISO8601DateString,
  getRange,
  getDateFormat,
} from 'helpers/dates'
import { saveLookupTablesFilters } from 'reducers/profile/reducer'
import {
  FILTER_TYPE_CREATED_BY,
  FILTER_TYPE_DATE_CREATED,
  FILTER_TYPE_DATE_CREATED_FROM,
  FILTER_TYPE_DATE_CREATED_TO,
  FILTER_TYPE_DATE_UPDATED,
  FILTER_TYPE_DATE_UPDATED_FROM,
  FILTER_TYPE_DATE_UPDATED_TO,
  FILTER_TYPE_MODIFIED_BY,
  FILTER_TYPE_STATUS,
  LOOKUP_TABLES_FILTERS,
  LOOKUP_TABLES_STATUS_FILTER,
} from 'constants/lookupTables'
import DateRangeInput, { THEME_MYJOB } from 'components/common/Form/DateRange'
import UsersSelectList from 'components/common/UsersSelectList/UsersSelectList'
import UsersListComponent from 'components/common/UsersList/UsersList'
import UsersListDropdown from 'components/common/UsersListDropdown/UsersListDropdown'
import FiltersDropdown from './FiltersDropdown'
import messages from '../translate'
import RemoveFilterButton from '../../ManagementUI/common/RemoveFilterButton/RemoveFilterButton'
import SingleSelectDropdown from './SingleSelectDropdown'

function SelectedFiltersDropdown({
  intl: { formatMessage },
  selectedFilters,
  setSelectedFilters,
  filtersList,
  filtersMenuAnchor,
  setFiltersMenuAnchor,
  createdBySearchQuery,
  modifiedBySearchQuery,
  setCreatedBySearchQuery,
  setModifiedBySearchQuery,
  tmpCreatedBy,
  tmpModifiedBy,
  setTmpCreatedBy,
  setTmpModifiedBy,
  filteredCreatedByListFilter,
  filteredModifiedByListFilter,
  lookupTableFilters,
  applyFilter,
  setIsUpdating,
  saveLookupTablesFilters,
  licensee: { usaDateFormat },
}) {
  const selectedFiltersData = selectedFilters.toJS()
  const filtersListData = filtersList.toJS()
  const {
    [FILTER_TYPE_CREATED_BY]: createdByFilter,
    [FILTER_TYPE_MODIFIED_BY]: modifiedByFilter,
  } = filtersListData
  const {
    [FILTER_TYPE_CREATED_BY]: appliedCreatedBy,
    [FILTER_TYPE_MODIFIED_BY]: appliedModifiedBy,
    [FILTER_TYPE_DATE_CREATED_FROM]: appliedDateCreatedFrom,
    [FILTER_TYPE_DATE_CREATED_TO]: appliedDateCreatedTo,
    [FILTER_TYPE_DATE_UPDATED_FROM]: appliedDateUpdatedFrom,
    [FILTER_TYPE_DATE_UPDATED_TO]: appliedDateUpdatedTo,
    [FILTER_TYPE_STATUS]: appliedStatus,
  } = lookupTableFilters
  const selectedCreatedBy = createdByFilter.filter(
    user =>
      appliedCreatedBy.length && appliedCreatedBy.find(id => user.id === id),
  )
  const selectedModifiedBy = modifiedByFilter.filter(
    user =>
      appliedModifiedBy.length && appliedModifiedBy.find(id => user.id === id),
  )

  const allFiltersSelected =
    selectedFiltersData.length === LOOKUP_TABLES_FILTERS.length

  const [prevCreatedBy, setPrevCreatedBy] = useState([])
  const [prevModifiedBy, setPrevModifiedBy] = useState([])
  const [filtersSubmenuAnchor, setFiltersSubmenuAnchor] = useState(null)
  const [createdByMenuAnchor, setCreatedByMenuAnchor] = useState(null)
  const [modifiedByMenuAnchor, setModifiedByMenuAnchor] = useState(null)
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null)

  const createdBySearchFieldRef = useRef()
  const modifiedBySearchFieldRef = useRef()

  const getUpdatedFiltersArr = filterType =>
    selectedFiltersData.filter(({ value }) => value !== filterType)

  const applyCreatedBy = (data = []) => {
    const saveFiltersPayload = {
      lookup_table_filters: {
        [FILTER_TYPE_CREATED_BY]: data,
      },
    }

    setIsUpdating(true)
    saveLookupTablesFilters(saveFiltersPayload)
  }

  const applyModifiedBy = (data = []) => {
    const saveFiltersPayload = {
      lookup_table_filters: {
        [FILTER_TYPE_MODIFIED_BY]: data,
      },
    }

    setIsUpdating(true)
    saveLookupTablesFilters(saveFiltersPayload)
  }

  const onRemoveCreatedByFilter = () => {
    const updatedFiltersArr = getUpdatedFiltersArr(FILTER_TYPE_CREATED_BY)

    setTmpCreatedBy([])
    setCreatedBySearchQuery('')
    setSelectedFilters(updatedFiltersArr)
    applyFilter({
      [FILTER_TYPE_CREATED_BY]: [],
    })
  }

  const onRemoveModifiedByFilter = () => {
    const updatedFiltersArr = getUpdatedFiltersArr(FILTER_TYPE_MODIFIED_BY)

    setTmpModifiedBy([])
    setModifiedBySearchQuery('')
    setSelectedFilters(updatedFiltersArr)
    applyFilter({
      [FILTER_TYPE_MODIFIED_BY]: [],
    })
  }

  const onRemoveStatusFilter = () => {
    const updatedFiltersArr = getUpdatedFiltersArr(FILTER_TYPE_STATUS)

    setSelectedFilters(updatedFiltersArr)
    applyFilter({
      [FILTER_TYPE_STATUS]: null,
    })
  }

  const onRemoveDateRange = (range, rangeFrom, rangeTo) => {
    const updatedFiltersArr = getUpdatedFiltersArr(range)

    setSelectedFilters(updatedFiltersArr)
    applyFilter({
      [rangeFrom]: null,
      [rangeTo]: null,
    })
  }

  const onCloseCreatedByMenu = () => {
    setCreatedByMenuAnchor(null)
    setCreatedBySearchQuery('')

    if (!isEqual(tmpCreatedBy, prevCreatedBy)) {
      const data = tmpCreatedBy.map(({ id }) => id)

      applyCreatedBy(data)
    }
  }

  const onCloseModifiedByMenu = () => {
    setModifiedByMenuAnchor(null)
    setModifiedBySearchQuery('')

    if (!isEqual(tmpModifiedBy, prevModifiedBy)) {
      const data = tmpModifiedBy.map(({ id }) => id)

      applyModifiedBy(data)
    }
  }

  const onCloseStatusMenu = value => {
    setStatusMenuAnchor(null)

    applyFilter({
      [FILTER_TYPE_STATUS]: value,
    })
  }

  const onApplyDateRange = ({ startDate, endDate }, rangeFrom, rangeTo) => {
    const formattedStartDate = dateToLocalISO8601DateString(startDate)
    const formattedEndDate = dateToLocalISO8601DateString(endDate)

    applyFilter({
      [rangeFrom]: formattedStartDate,
      [rangeTo]: formattedEndDate,
    })
  }

  const renderFilteredUserList = (list, tmpList, setTmpList, title) => (
    <div>
      {!!list.length && (
        <>
          <ListItem className="menu-title secondary">{title}</ListItem>
          <UsersSelectList
            list={list}
            tmpUsersArr={tmpList}
            setTmpUsersArr={setTmpList}
          />
        </>
      )}
      {!list.length && (
        <ListItem>{formatMessage(messages.noResultsMessage)}</ListItem>
      )}
    </div>
  )

  const renderSelectedFilterInput = ({ value, label }) => {
    switch (value) {
      case FILTER_TYPE_CREATED_BY:
        return (
          <ListItem key={value} className="other-filter-input-holder">
            <div className="other-filter-label">
              {formatMessage(messages[label])}
            </div>
            <Button
              className="other-filter-input dropdown"
              onClick={({ currentTarget }) => {
                setCreatedByMenuAnchor(currentTarget)
                setPrevCreatedBy(tmpCreatedBy)
                setTimeout(() => {
                  if (createdBySearchFieldRef.current) {
                    createdBySearchFieldRef.current.focus()
                  }
                }, 0)
              }}>
              <UsersListComponent users={selectedCreatedBy} />
            </Button>
            <UsersListDropdown
              anchor={createdByMenuAnchor}
              onCloseMenu={onCloseCreatedByMenu}
              searchQuery={createdBySearchQuery}
              setSearchQuery={setCreatedBySearchQuery}
              inputRef={createdBySearchFieldRef}
              renderUserList={() =>
                renderFilteredUserList(
                  filteredCreatedByListFilter,
                  tmpCreatedBy,
                  setTmpCreatedBy,
                  formatMessage(messages.createdBy),
                )
              }
            />
            <RemoveFilterButton removeFn={onRemoveCreatedByFilter} />
          </ListItem>
        )
      case FILTER_TYPE_MODIFIED_BY:
        return (
          <ListItem key={value} className="other-filter-input-holder">
            <div className="other-filter-label">
              {formatMessage(messages[label])}
            </div>
            <Button
              className="other-filter-input dropdown"
              onClick={({ currentTarget }) => {
                setModifiedByMenuAnchor(currentTarget)
                setPrevModifiedBy(tmpModifiedBy)
                setTimeout(() => {
                  if (modifiedBySearchFieldRef.current) {
                    modifiedBySearchFieldRef.current.focus()
                  }
                }, 0)
              }}>
              <UsersListComponent users={selectedModifiedBy} />
            </Button>
            <UsersListDropdown
              anchor={modifiedByMenuAnchor}
              onCloseMenu={onCloseModifiedByMenu}
              searchQuery={modifiedBySearchQuery}
              setSearchQuery={setModifiedBySearchQuery}
              inputRef={modifiedBySearchFieldRef}
              renderUserList={() =>
                renderFilteredUserList(
                  filteredModifiedByListFilter,
                  tmpModifiedBy,
                  setTmpModifiedBy,
                  formatMessage(messages.modifiedBy),
                )
              }
            />
            <RemoveFilterButton removeFn={onRemoveModifiedByFilter} />
          </ListItem>
        )
      case FILTER_TYPE_STATUS:
        return (
          <ListItem key={value} className="other-filter-input-holder">
            <div className="other-filter-label">
              {formatMessage(messages[label])}
            </div>
            <Button
              className="other-filter-input dropdown"
              onClick={({ currentTarget }) => {
                setStatusMenuAnchor(currentTarget)
              }}>
              {appliedStatus}
            </Button>
            <SingleSelectDropdown
              anchor={statusMenuAnchor}
              onCloseMenu={onCloseStatusMenu}
              arr={LOOKUP_TABLES_STATUS_FILTER}
              selectedValue={appliedStatus}
            />
            <RemoveFilterButton removeFn={onRemoveStatusFilter} />
          </ListItem>
        )
      case FILTER_TYPE_DATE_CREATED:
        return (
          <ListItem key={value} className="other-filter-input-holder">
            <div className="other-filter-label">
              {formatMessage(messages[label])}
            </div>
            <DateRangeInput
              dateDisplayFormat={getDateFormat(usaDateFormat)}
              styleTheme={THEME_MYJOB}
              label={`${formatMessage(messages.start)} / ${formatMessage(
                messages.end,
              )}`}
              range={[
                {
                  key: 'selection',
                  startDate: appliedDateCreatedFrom
                    ? new Date(appliedDateCreatedFrom)
                    : new Date(),
                  endDate: appliedDateCreatedTo
                    ? new Date(appliedDateCreatedTo)
                    : new Date(),
                },
              ]}
              inputProps={{
                variant: 'outlined',
              }}
              minWidth={300}
              dateRangeProps={{
                showMonthAndYearPickers: true,
                maxDate: new Date(),
              }}
              staticRanges={[
                getRange(
                  formatMessage(messages.oneDay),
                  new Date(),
                  new Date(),
                ),
                getRange(
                  formatMessage(messages.threeDays),
                  new Date(),
                  addDays(new Date(), 2),
                ),
                getRange(
                  formatMessage(messages.oneWeek),
                  new Date(),
                  subDays(addWeeks(new Date(), 1), 1),
                ),
                getRange(
                  formatMessage(messages.twoWeeks),
                  new Date(),
                  subDays(addWeeks(new Date(), 2), 1),
                ),
                getRange(
                  formatMessage(messages.oneMonth),
                  new Date(),
                  subDays(addMonths(new Date(), 1), 1),
                ),
              ]}
              onClose={value => {
                onApplyDateRange(
                  value,
                  FILTER_TYPE_DATE_CREATED_FROM,
                  FILTER_TYPE_DATE_CREATED_TO,
                )
              }}
            />
            <RemoveFilterButton
              removeFn={() => {
                onRemoveDateRange(
                  FILTER_TYPE_DATE_CREATED,
                  FILTER_TYPE_DATE_CREATED_FROM,
                  FILTER_TYPE_DATE_CREATED_TO,
                )
              }}
            />
          </ListItem>
        )
      case FILTER_TYPE_DATE_UPDATED:
        return (
          <ListItem key={value} className="other-filter-input-holder">
            <div className="other-filter-label">
              {formatMessage(messages[label])}
            </div>
            <DateRangeInput
              dateDisplayFormat={getDateFormat(usaDateFormat)}
              styleTheme={THEME_MYJOB}
              label={`${formatMessage(messages.start)} / ${formatMessage(
                messages.end,
              )}`}
              range={[
                {
                  key: 'selection',
                  startDate: appliedDateUpdatedFrom
                    ? new Date(appliedDateUpdatedFrom)
                    : new Date(),
                  endDate: appliedDateUpdatedTo
                    ? new Date(appliedDateUpdatedTo)
                    : new Date(),
                },
              ]}
              inputProps={{
                variant: 'outlined',
              }}
              minWidth={300}
              dateRangeProps={{
                showMonthAndYearPickers: true,
                maxDate: new Date(),
              }}
              staticRanges={[
                getRange(
                  formatMessage(messages.oneDay),
                  new Date(),
                  new Date(),
                ),
                getRange(
                  formatMessage(messages.threeDays),
                  new Date(),
                  addDays(new Date(), 2),
                ),
                getRange(
                  formatMessage(messages.oneWeek),
                  new Date(),
                  subDays(addWeeks(new Date(), 1), 1),
                ),
                getRange(
                  formatMessage(messages.twoWeeks),
                  new Date(),
                  subDays(addWeeks(new Date(), 2), 1),
                ),
                getRange(
                  formatMessage(messages.oneMonth),
                  new Date(),
                  subDays(addMonths(new Date(), 1), 1),
                ),
              ]}
              onClose={value => {
                onApplyDateRange(
                  value,
                  FILTER_TYPE_DATE_UPDATED_FROM,
                  FILTER_TYPE_DATE_UPDATED_TO,
                )
              }}
            />
            <RemoveFilterButton
              removeFn={() => {
                onRemoveDateRange(
                  FILTER_TYPE_DATE_UPDATED,
                  FILTER_TYPE_DATE_UPDATED_FROM,
                  FILTER_TYPE_DATE_UPDATED_TO,
                )
              }}
            />
          </ListItem>
        )
      default:
        return (
          <ListItem key={value} className="wide">
            {formatMessage(messages[label])}
          </ListItem>
        )
    }
  }

  return (
    <Menu
      keepMounted
      anchorEl={filtersMenuAnchor}
      open={!!filtersMenuAnchor}
      onClose={() => {
        setFiltersMenuAnchor(null)
      }}
      className="management-ui-menu">
      <ListItem className="menu-title">
        {formatMessage(messages.activeFilters)}
      </ListItem>
      {selectedFiltersData.map(filterItem =>
        renderSelectedFilterInput(filterItem),
      )}
      {!allFiltersSelected && (
        <ListItem>
          <span
            className="add-filter-button"
            onClick={({ currentTarget }) => {
              setFiltersSubmenuAnchor(
                currentTarget
                  .closest('.MuiMenu-paper')
                  .getElementsByClassName('submenu-anchor-el')[0],
              )
            }}>
            {formatMessage(messages.addFilter)}
          </span>
          <FiltersDropdown
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            anchorEl={filtersSubmenuAnchor}
            applyFilter={applyFilter}
            setAnchorEl={setFiltersSubmenuAnchor}
            closeOnSelect
          />
        </ListItem>
      )}
      <ListItem className="submenu-anchor-el" />
    </Menu>
  )
}

export default connect(
  state => ({
    filtersList: state.getIn(['lookupTables', 'filtersList']),
    selectedFilters: state.getIn(['lookupTables', 'selectedFilters']),
    lookupTableFilters: state.getIn([
      'profile',
      'profile',
      'lookupTableFilters',
    ]),
    licensee: state.getIn(['profile', 'profile', 'licensee']),
  }),
  {
    setSelectedFilters,
    saveLookupTablesFilters,
  },
)(injectIntl(SelectedFiltersDropdown))
